'use client';

import Image from 'next/image';
import { useReducedMotion } from 'motion/react';
import { useRef, useState, useSyncExternalStore, useEffect, type CSSProperties, type ReactNode } from 'react';
import { gsap, useGSAP, ScrollTrigger } from '../lib/gsap';
import { DESKTOP_DESIGN_HEIGHT_PX, DESKTOP_DESIGN_WIDTH_PX, useWidthScale } from '../lib/desktop-scale';
import { getViewportHeight, subscribeViewportHeight } from '../lib/viewport-height';

const ASPECT_W = DESKTOP_DESIGN_WIDTH_PX;
const ASPECT_H = DESKTOP_DESIGN_HEIGHT_PX;

/*
 * Artwork WITHOUT text. The headline used to be outlined vector text baked into the SVG/PNGs (in the old
 * Cal Sans), so it could not follow a text style, be selected, be read by a screen reader or be indexed.
 * The four text paths were removed from the exported art and the copy is now live text, positioned from
 * the Figma frame ("Desktop - 7" › night / day) and styled with design-system text styles.
 */
const NIGHT_ART = '/images/night-art.svg';
const DAY_ART = '/images/day-art.svg';

/** Feather illustration top/bottom into page white (mask softens pixels; overlays match white). */
const VERTICAL_EDGE_MASK =
  'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.25) 6%, rgba(0,0,0,0.72) 12%, black 18%, black 82%, rgba(0,0,0,0.72) 88%, rgba(0,0,0,0.25) 94%, transparent 100%)'; // lint-colors-ignore: alpha mask, only the alpha channel is used

const imageStackMaskStyle: CSSProperties = {
  maskImage: VERTICAL_EDGE_MASK,
  WebkitMaskImage: VERTICAL_EDGE_MASK,
  maskSize: '100% 100%',
  WebkitMaskSize: '100% 100%',
};

const imageBlendGradients = 'pointer-events-none absolute inset-0 z-10';
const edgeBleedHeight = 'h-[16%]';
/** White fading to transparent, built from the design-system white (color/white) so it stays bound to the palette. */
const whiteAt = (pct: number) => `color-mix(in srgb, var(--ds-white) ${pct}%, transparent)`;
const blendGradient = (direction: string) =>
  `linear-gradient(${direction}, ${whiteAt(100)} 0%, ${whiteAt(96)} 14%, ${whiteAt(78)} 32%, ${whiteAt(45)} 52%, ${whiteAt(18)} 72%, transparent 100%)`;
const topBlendStyle: CSSProperties = {
  background:
    blendGradient('to bottom'),
};
const bottomBlendStyle: CSSProperties = {
  background:
    blendGradient('to top'),
};
const topBlend = `absolute left-0 right-0 top-0 ${edgeBleedHeight}`;
const bottomBlend = `absolute left-0 right-0 bottom-0 ${edgeBleedHeight}`;

/**
 * The mobile crop of the illustration: the phone shows the same scene as the desktop art at ~0.95 scale,
 * centred a little left of the middle (matches the previous portrait PNGs).
 */
const MOBILE_ART_CLASS = 'object-cover';
const MOBILE_ART_STYLE: CSSProperties = { objectPosition: '46% 50%' };

function useViewportHeight(): number {
  return useSyncExternalStore(
    subscribeViewportHeight,
    () => getViewportHeight(),
    () => 800
  );
}

function BlendOverlays() {
  return (
    <div className={imageBlendGradients} aria-hidden>
      <div className={topBlend} style={topBlendStyle} />
      <div className={bottomBlend} style={bottomBlendStyle} />
    </div>
  );
}

function mobileSectionStyle(viewportH: number | undefined): CSSProperties {
  return {
    '--night-mobile-vh': viewportH ? `${viewportH}px` : '100svh',
  } as CSSProperties;
}

/** Text authored on the 1440×888 canvas, scaled with the artwork (which is always the viewport width). */
function DesktopCanvasText({ scale, children }: { scale: number; children: ReactNode }) {
  return (
    <div
      className="pointer-events-none absolute left-0 top-0 origin-top-left"
      style={{ width: ASPECT_W, height: ASPECT_H, transform: `scale(${scale})` }}
    >
      {children}
    </div>
  );
}

/** Figma "night": eyebrow row at (675, 346) and a 920px centred headline at (260, 370). White text. */
function DesktopNightCopy({ scale }: { scale: number }) {
  return (
    <DesktopCanvasText scale={scale}>
      <p
        className="absolute border-l-2 border-prople-500 pl-2 ds-text-title-base text-white"
        style={{ left: 675, top: 346 }}
      >
        The problem
      </p>
      <h2
        className="absolute ds-text-title-6xl text-center text-white"
        style={{ left: 260, top: 370, width: 920 }}
      >
        Real estate builds wealth. But 95% of Nigerians can&apos;t access it.
      </h2>
    </DesktopCanvasText>
  );
}

/** Figma "day": "...until now", 60px, centred on the canvas at y=401. */
function DesktopDayCopy({ scale }: { scale: number }) {
  return (
    <DesktopCanvasText scale={scale}>
      <p className="absolute ds-text-title-6xl text-center text-white whitespace-nowrap" style={{ left: 0, top: 401, width: ASPECT_W }}>
        ...until now
      </p>
    </DesktopCanvasText>
  );
}

/** Phones: the same copy, centred, one step down the scale (24px) so the headline fits three short lines. */
function MobileNightCopy() {
  return (
    <div className="absolute inset-0 z-[3] flex flex-col items-center justify-center px-5 pb-[6%] text-center text-white">
      <p className="mb-3 border-l-2 border-prople-500 pl-[6px] ds-text-title-base">The problem</p>
      <h2 className="ds-text-title-2xl max-w-[350px]">
        Real estate builds wealth. But 95% of Nigerians can&apos;t access it.
      </h2>
    </div>
  );
}

function MobileDayCopy() {
  return (
    <div className="absolute inset-0 z-[3] flex items-center justify-center px-5 text-center text-white">
      <p className="ds-text-title-2xl">...until now</p>
    </div>
  );
}

export default function NightToDaySection() {
  const prefersReduce = useReducedMotion();
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isCapturingScroll, setIsCapturingScroll] = useState(false);
  const viewportH = useViewportHeight();
  const widthScale = useWidthScale();
  const mobileSectionHeight =
    viewportH > 0 ? viewportH : undefined;

  useGSAP(
    () => {
      const section = sectionRef.current;
      if (!section || prefersReduce) return;

      const mobileNight = section.querySelector<HTMLElement>('[data-mobile-night]');
      const mobileDay = section.querySelector<HTMLElement>('[data-mobile-day]');

      const desktopNightLayers = section.querySelectorAll<HTMLElement>(
        '[data-desktop-night-layer]'
      );
      const desktopDayLayers = section.querySelectorAll<HTMLElement>(
        '[data-desktop-day-layer]'
      );

      const refresh = () => ScrollTrigger.refresh();
      const images = section.querySelectorAll('img');
      images.forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', refresh, { once: true });
        }
      });

      const mm = gsap.matchMedia();

      // Desktop/tablet: pinned scroll-scrub transition.
      mm.add('(min-width: 640px)', () => {
        if (!desktopNightLayers.length || !desktopDayLayers.length) {
          return () => {};
        }

        gsap.set(desktopNightLayers, { opacity: 1 });
        gsap.set(desktopDayLayers, { opacity: 0 });

        const desktopLayers = [...desktopNightLayers, ...desktopDayLayers];

        // Hold on the night scene for the first 30% of this pin's scroll before the crossfade to day
        // starts (mirrors the mobile branch's own HOLD_FRAC below) — previously the fade began
        // immediately at scroll position 0, so "...until now" started appearing right away.
        const HOLD_FRAC = 0.3;
        const FADE_FRAC = 1 - HOLD_FRAC;

        const tween = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: section,
            start: 'center center',
            end: '+=120%',
            pin: true,
            pinSpacing: true,
            scrub: 0.45,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onToggle: (self) => {
              setIsCapturingScroll(self.isActive);
              gsap.set(desktopLayers, {
                willChange: self.isActive ? 'opacity' : 'auto',
              });
            },
          },
        });

        tween
          .to(desktopNightLayers, { opacity: 0, duration: FADE_FRAC }, HOLD_FRAC)
          .to(desktopDayLayers, { opacity: 1, duration: FADE_FRAC }, HOLD_FRAC);

        return () => {
          tween.scrollTrigger?.kill();
          tween.kill();
        };
      });

      // Mobile: pin full-viewport, hold night, then scrub crossfade.
      mm.add('(max-width: 639px)', () => {
        if (!mobileNight || !mobileDay) {
          return () => {};
        }

        const HOLD_FRAC = 0.3;

        const applyMobileFade = (progress: number) => {
          const fade =
            progress <= HOLD_FRAC
              ? 0
              : (progress - HOLD_FRAC) / (1 - HOLD_FRAC);
          mobileNight.style.opacity = String(1 - fade);
          mobileDay.style.opacity = String(fade);
        };

        gsap.set(mobileNight, { opacity: 1 });
        gsap.set(mobileDay, { opacity: 0 });

        const st = ScrollTrigger.create({
          trigger: section,
          start: 'top top',
          end: '+=150%',
          pin: true,
          pinSpacing: true,
          scrub: 0.45,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => applyMobileFade(self.progress),
          onToggle: (self) => {
            setIsCapturingScroll(self.isActive);
            const willChange = self.isActive ? 'opacity' : 'auto';
            mobileNight.style.willChange = willChange;
            mobileDay.style.willChange = willChange;
          },
        });

        applyMobileFade(st.progress);

        return () => {
          st.kill();
          mobileNight.style.opacity = '';
          mobileDay.style.opacity = '';
          mobileNight.style.willChange = '';
          mobileDay.style.willChange = '';
        };
      });

      return () => {
        images.forEach((img) => img.removeEventListener('load', refresh));
        mm.revert();
      };
    },
    { scope: sectionRef, dependencies: [prefersReduce], revertOnUpdate: true }
  );

  useEffect(() => {
    if (prefersReduce) return;
    ScrollTrigger.refresh();
  }, [viewportH, prefersReduce]);

  if (prefersReduce) {
    // Reduced motion: no pinned crossfade — night, then day, stacked and static.
    return (
      <section
        className="relative mt-0 h-[var(--night-mobile-vh,100svh)] min-h-[var(--night-mobile-vh,100svh)] w-full shrink-0 sm:mt-[10vh] sm:h-auto sm:min-h-0"
        style={mobileSectionStyle(mobileSectionHeight)}
        aria-label="Day and night illustration"
      >
        <div className="relative h-full w-full overflow-hidden bg-white sm:hidden">
          <div className="absolute inset-0" style={imageStackMaskStyle}>
            <div className="absolute inset-0 bg-blue-950" aria-hidden />
            <Image
              src={NIGHT_ART}
              alt=""
              fill
              className={MOBILE_ART_CLASS}
              style={MOBILE_ART_STYLE}
              sizes="100vw"
              priority
            />
            <MobileNightCopy />
          </div>
          <BlendOverlays />
        </div>

        <div className="hidden w-full sm:block">
          <div className="relative w-full overflow-hidden">
            <div style={imageStackMaskStyle} className="relative">
              <Image
                src={NIGHT_ART}
                alt=""
                width={ASPECT_W}
                height={ASPECT_H}
                className="block h-auto w-full max-w-none"
                sizes="100vw"
                priority
              />
              <DesktopNightCopy scale={widthScale} />
            </div>
            <BlendOverlays />
          </div>
          <div className="relative w-full overflow-hidden">
            <div style={imageStackMaskStyle} className="relative">
              <Image
                src={DAY_ART}
                alt=""
                width={ASPECT_W}
                height={ASPECT_H}
                className="block h-auto w-full max-w-none"
                sizes="100vw"
              />
              <DesktopDayCopy scale={widthScale} />
            </div>
            <BlendOverlays />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="night-to-day-section"
      ref={sectionRef}
      className="relative mt-0 h-[var(--night-mobile-vh,100svh)] min-h-[var(--night-mobile-vh,100svh)] w-full shrink-0 sm:mt-[10vh] sm:h-[100svh] sm:min-h-[100svh]"
      style={mobileSectionStyle(mobileSectionHeight)}
      aria-label="Scroll to transition from night to day"
      aria-busy={isCapturingScroll}
    >
      <div className="relative h-full w-full overflow-hidden bg-white sm:hidden">
        <div
          data-mobile-night
          className="absolute inset-0 z-[1]"
          style={imageStackMaskStyle}
        >
          <div className="absolute inset-0 bg-blue-950" aria-hidden />
          <Image
            src={NIGHT_ART}
            alt=""
            fill
            className={MOBILE_ART_CLASS}
            style={MOBILE_ART_STYLE}
            sizes="100vw"
            priority
          />
          <MobileNightCopy />
        </div>
        <div
          data-mobile-day
          className="absolute inset-0 z-[2]"
          style={imageStackMaskStyle}
        >
          <div className="absolute inset-0 bg-blue-300" aria-hidden />
          <Image
            src={DAY_ART}
            alt=""
            fill
            className={MOBILE_ART_CLASS}
            style={MOBILE_ART_STYLE}
            sizes="100vw"
          />
          <MobileDayCopy />
        </div>
        <BlendOverlays />
      </div>

      <div className="relative hidden h-full w-full items-center justify-center overflow-hidden sm:flex">
        <div
          className="relative w-full"
          style={{ aspectRatio: `${ASPECT_W} / ${ASPECT_H}` }}
        >
          <div className="absolute inset-0" style={imageStackMaskStyle}>
            <div data-desktop-night-layer className="absolute inset-0">
              <Image
                src={NIGHT_ART}
                alt=""
                fill
                className="object-cover object-center"
                sizes="100vw"
                priority
              />
              <DesktopNightCopy scale={widthScale} />
            </div>
            <div data-desktop-day-layer className="absolute inset-0">
              <Image
                src={DAY_ART}
                alt=""
                fill
                className="object-cover object-center"
                sizes="100vw"
              />
              <DesktopDayCopy scale={widthScale} />
            </div>
          </div>
          <BlendOverlays />
        </div>
      </div>
    </section>
  );
}
